import * as React from "react";
import { Palette, RefreshCw, Image as ImageIcon, X, ZoomIn, PanelRightOpen, PanelRightClose } from "lucide-react";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";
import { useSpaceStore } from "../../stores/spaceStore";
import { getProductTexture, runMapping } from "../../services/api";
import { useToast } from "../ui/Toast";
import { formatSurfaceLabelFromRaw } from "../../lib/utils";
import type { SurfaceZone } from "../../types";

/** A pill in the surface picker: one surface, a saved zone, or the whole staircase. */
interface MappingTarget {
  key: string;
  label: string;
  surfaces: string[]; // session labels ("floor", "wall2", "stairs_3")
}

/** Zone members are geometry keys ("walls_1"); the session labels them "wall2". */
function zoneMemberToLabel(member: string): string {
  const wall = /^walls_(\d+)$/.exec(member);
  if (wall) return `wall${parseInt(wall[1], 10) + 1}`;
  if (member === "floor_0") return "floor";
  if (member === "ceiling_0") return "ceiling";
  return member; // stairs_<i> is already its session label
}

/**
 * Group the picker like the visualizer does: each saved zone is one pill,
 * stairs pieces no zone claims are one "Stairs" pill, everything else keeps
 * its own pill. Zone members this session no longer has are dropped.
 */
function buildMappingTargets(labels: string[], zones: SurfaceZone[] | undefined): MappingTarget[] {
  const available = new Set(labels);
  const claimed = new Set<string>();
  const targets: MappingTarget[] = [];
  (zones || []).forEach((zone, i) => {
    const members = zone.surfaces.map(zoneMemberToLabel).filter((l) => available.has(l) && !claimed.has(l));
    if (!members.length) return;
    members.forEach((l) => claimed.add(l));
    targets.push({ key: `zone:${zone.id}`, label: zone.label || `Zone ${i + 1}`, surfaces: members });
  });
  const stairs = labels.filter((l) => l.startsWith("stairs") && !claimed.has(l));
  labels.forEach((l) => {
    if (claimed.has(l) || l.startsWith("stairs")) return;
    targets.push({ key: l, label: formatSurfaceLabelFromRaw(l), surfaces: [l] });
  });
  if (stairs.length) targets.push({ key: "stairs:all", label: "Stairs", surfaces: stairs });
  return targets;
}

interface TextureMappingProps {
  selectedProduct: string;
  onProductSelect: (productId: string) => void;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
}

export function TextureMapping({ selectedProduct, onProductSelect, sidebarOpen, onToggleSidebar }: TextureMappingProps) {
  const [texturePreview, setTexturePreview] = React.useState<string | null>(null);
  const [resultImage, setResultImage] = React.useState<string | null>(null);
  const [selectedSurfaces, setSelectedSurfaces] = React.useState<string[]>([]);
  const [showTextureDialog, setShowTextureDialog] = React.useState(false);
  const [showResultDialog, setShowResultDialog] = React.useState(false);
  const [maxDimension, setMaxDimension] = React.useState<string>("");
  const [mappingTime, setMappingTime] = React.useState<number | null>(null);
  const [rotation, setRotation] = React.useState<string>("0");

  const [isLoadingTexture, setIsLoadingTexture] = React.useState(false);
  const [isMapping, setIsMapping] = React.useState(false);

  const { surfaces, spaceId, originalImage, spaceInfo } = useSpaceStore();
  const targets = React.useMemo(
    () => buildMappingTargets(surfaces.map((s) => s.label), spaceInfo?.zones),
    [surfaces, spaceInfo?.zones]
  );
  // Picker state is per pill; the surfaces actually mapped are the union of the picked pills
  const [selectedTargets, setSelectedTargets] = React.useState<string[]>([]);
  React.useEffect(() => {
    setSelectedSurfaces(
      targets.filter((t) => selectedTargets.includes(t.key)).flatMap((t) => t.surfaces)
    );
  }, [targets, selectedTargets]);
  const { addToast } = useToast();

  // Track previous product to detect changes from sidebar
  const prevProductRef = React.useRef(selectedProduct);
  React.useEffect(() => {
    if (selectedProduct && selectedProduct !== prevProductRef.current) {
      prevProductRef.current = selectedProduct;
      loadTexture(selectedProduct);
    }
  }, [selectedProduct]);

  const loadTexture = async (productId: string) => {
    setIsLoadingTexture(true);
    try {
      const blob = await getProductTexture(productId, true, spaceId || undefined);
      const url = URL.createObjectURL(blob);
      setTexturePreview(url);
      addToast("success", "Texture loaded and set");
    } catch (error) {
      addToast("error", "Failed to load texture");
    } finally {
      setIsLoadingTexture(false);
    }
  };

  const handleTargetToggle = (key: string) => {
    setSelectedTargets((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const allTargetsSelected = targets.length > 0 && selectedTargets.length === targets.length;
  const handleSelectAll = () => {
    setSelectedTargets(allTargetsSelected ? [] : targets.map((t) => t.key));
  };

  const handleRunMapping = async () => {
    if (!selectedProduct) {
      addToast("error", "Please select a product first");
      return;
    }

    if (selectedSurfaces.length === 0) {
      addToast("error", "Please select at least one surface");
      return;
    }

    setIsMapping(true);
    setMappingTime(null);
    const startTime = performance.now();
    try {
      // Convert surface format from "wall1", "wall2" to "wall_0", "wall_1" for API
      const apiSurfaces = selectedSurfaces.map((s) => {
        const match = s.match(/^wall(\d+)$/i);
        if (match) {
          return `wall_${parseInt(match[1], 10) - 1}`;
        }
        return s;
      });
      const sideMax = maxDimension ? parseInt(maxDimension, 10) : undefined;
      const rotationValue = rotation ? parseInt(rotation, 10) : 0;
      const response = await runMapping(apiSurfaces, sideMax, spaceId ?? undefined, rotationValue, selectedProduct || undefined) as any;

      const endTime = performance.now();
      const elapsedTime = (endTime - startTime) / 1000; // Convert to seconds
      setMappingTime(elapsedTime);

      if (response.status === "SUCCESS") {
        // API returns image in 'output' or 'result_image' field
        const imageBase64 = response.output || response.result_image;
        if (imageBase64) {
          setResultImage(`data:image/png;base64,${imageBase64}`);
          addToast("success", `Mapping completed in ${elapsedTime.toFixed(2)}s`);
        } else {
          throw new Error("No result image returned");
        }
      } else {
        throw new Error(response.message || "Failed to run mapping");
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Failed to run mapping";
      addToast("error", message);
    } finally {
      setIsMapping(false);
    }
  };

  if (!spaceId) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Load a space first to apply textures
        </CardContent>
      </Card>
    );
  }


  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Palette className="h-5 w-5" />
          Texture Mapping
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Top Row: Texture preview, Surfaces, Rotation, Max dimension, Visualize */}
        <div className="flex items-end gap-3">
          {/* Texture Preview + Product panel toggle */}
          <div className="flex items-center gap-1.5">
            {isLoadingTexture ? (
              <div className="h-9 w-9 bg-muted rounded border flex items-center justify-center">
                <RefreshCw className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            ) : texturePreview ? (
              <button
                onClick={() => setShowTextureDialog(true)}
                className="h-9 w-9 bg-muted rounded border overflow-hidden hover:ring-2 hover:ring-primary/50 transition-all"
              >
                <img src={texturePreview} alt="Texture" className="w-full h-full object-cover" />
              </button>
            ) : (
              <div className="h-9 w-9 bg-muted rounded border flex items-center justify-center">
                <ImageIcon className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
            <button
              onClick={onToggleSidebar}
              className={`h-9 w-9 rounded border flex items-center justify-center transition-all ${
                sidebarOpen
                  ? "bg-primary/10 border-primary/30 text-primary"
                  : "bg-muted border-input text-muted-foreground hover:text-foreground hover:border-muted-foreground/50"
              }`}
              title={sidebarOpen ? "Close product panel" : "Browse products"}
            >
              {sidebarOpen ? (
                <PanelRightClose className="h-4 w-4" />
              ) : (
                <PanelRightOpen className="h-4 w-4" />
              )}
            </button>
          </div>

          {/* Surfaces - elegant toggle pills */}
          <div className="flex items-center gap-1 rounded-lg bg-muted/50 p-1">
            {targets.map((target) => {
              const isSelected = selectedTargets.includes(target.key);
              return (
                <button
                  key={target.key}
                  onClick={() => handleTargetToggle(target.key)}
                  title={target.surfaces.length > 1 ? target.surfaces.map(formatSurfaceLabelFromRaw).join(", ") : undefined}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all ${
                    isSelected
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}
                >
                  {target.label}
                  {target.surfaces.length > 1 && <span className="ml-1 opacity-70">({target.surfaces.length})</span>}
                </button>
              );
            })}
            <div className="w-px h-4 bg-border mx-0.5" />
            <button
              onClick={handleSelectAll}
              className="px-2 py-1 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-all"
            >
              {allTargetsSelected ? "Clear" : "All"}
            </button>
          </div>

          {/* Spacer to push right items */}
          <div className="flex-1" />

          {/* Rotation Toggle */}
          <div className="flex items-center gap-1 rounded-lg bg-muted/50 p-1">
            <button
              onClick={() => setRotation("0")}
              className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                rotation === "0"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
              title="No rotation"
            >
              0°
            </button>
            <button
              onClick={() => setRotation("90")}
              className={`px-2 py-1 rounded-md text-xs font-medium transition-all ${
                rotation === "90"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground hover:bg-muted"
              }`}
              title="Rotate 90 degrees"
            >
              90°
            </button>
          </div>

          {/* Max Dimension */}
          <div className="flex items-center gap-2">
            <Input
              type="number"
              placeholder="Max px"
              value={maxDimension}
              onChange={(e) => setMaxDimension(e.target.value)}
              className="w-24 h-9 text-sm"
              min={100}
              max={5000}
              title="Max dimension (leave empty for full res)"
            />
            {/* Run Button */}
            <Button
              onClick={handleRunMapping}
              loading={isMapping}
              disabled={!selectedProduct || selectedSurfaces.length === 0}
              size="sm"
            >
              <Palette className="h-4 w-4 mr-1" />
              Visualize
            </Button>
          </div>
        </div>

        {/* Preview Images - side by side, smaller */}
        <div className="grid grid-cols-2 gap-3">
          {/* Original */}
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">Original</p>
            <div className="aspect-video bg-muted rounded overflow-hidden border">
              {originalImage ? (
                <img src={originalImage} alt="Original" className="w-full h-full object-contain" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                  No image
                </div>
              )}
            </div>
          </div>

          {/* Result */}
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground flex items-center gap-2">
              Result
              {mappingTime !== null && (
                <span className="text-xs font-mono bg-primary/10 text-primary px-1.5 py-0.5 rounded">
                  {mappingTime.toFixed(2)}s
                </span>
              )}
            </p>
            {resultImage ? (
              <button
                onClick={() => setShowResultDialog(true)}
                className="aspect-video w-full bg-muted rounded overflow-hidden border hover:ring-2 hover:ring-primary/50 transition-all group relative"
              >
                <img src={resultImage} alt="Result" className="w-full h-full object-contain" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <ZoomIn className="h-5 w-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            ) : (
              <div className="aspect-video bg-muted rounded overflow-hidden border flex items-center justify-center text-xs text-muted-foreground">
                Run mapping to see result
              </div>
            )}
          </div>
        </div>

        {/* Texture Preview Dialog */}
        {showTextureDialog && texturePreview && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
            onClick={() => setShowTextureDialog(false)}
          >
            <div className="relative max-w-[90vw] max-h-[90vh]">
              <img
                src={texturePreview}
                alt="Texture preview full"
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={() => setShowTextureDialog(false)}
                className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Result Image Dialog */}
        {showResultDialog && resultImage && (
          <div
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
            onClick={() => setShowResultDialog(false)}
          >
            <div className="relative max-w-[90vw] max-h-[90vh]">
              <img
                src={resultImage}
                alt="Result full"
                className="max-w-full max-h-[90vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={() => setShowResultDialog(false)}
                className="absolute top-2 right-2 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
